import React, { useEffect, useMemo, useState } from 'react';
import { Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import _, { debounce } from 'lodash';
import { selectUser } from '../../../reducers/user.selectors';
import {
  getProductLoadingState as getProductLoadingStateSelector,
  getMindItemLoadingState as getMindItemLoadingStateSelector,
  getVideoMindItemsLoadingState as getVideoMindItemsLoadingStateSelector,
  SearchLoadingStatus,
  getPaginationSelector,
  getUnifiedPaginationSelector,
  getCourseMindItemsLoadingState,
  getUnifiedLoadingState,
} from '../../../reducers/search.selectors';
import './searchResults.scss';

import { zIndex } from '../../../constants';
import MuTabs from '../../common/MuTabs';
import MuTabsNewUI from '../../common/MuTabsNewUI';
import { SearchFilters, SearchResultsTab as SearchResultsTabEnum } from '../../../types/search';
import { MindItemSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { palette } from '../../../assets/styles/themes/tokens';
import { Box, Divider } from '@mui/material';

import { MuiCardCommonProps } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import { ProductSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/Product';
import { useNewDesign } from '../../../state/DesignPreferenceContextProvider';
import { ItemsTab } from '../../common/MuTabsNewUI/MuTabsNewUI';
import searchResultsStorageManager from '../../../utils/searchResultsStorageManager';
import { SearchCompleted } from '../../../ampli';
import { getAmpliSearchEventPayload, mapDesignPreference, SearchEventData } from '../../../utils/ampli';
import services from '../../../services';
import SearchResultsTab from './SearchResultsTabs';
import { useNewUiSearchPagePadding } from '../../../routes/courses/CourseSearchPage/CourseSearchPage';
import { useNewNavigation } from '../../../utils/useNewNavigation';
import useCardDimensions from '../../../utils/useCardDimensions';

export type SearchResultsItemsTab = ItemsTab<SearchResultsTabEnum> & { loadMore: () => void };

interface SearchResultsProps {
  currentTab: SearchResultsTabEnum;
  requestFilters: SearchFilters;
  trackingPayload: SearchEventData;
  tabs: SearchResultsItemsTab[]
  setCurrentTab?: (tab: SearchResultsTabEnum) => void;
  searchTerm?: string;
  forceNewDesign?: boolean;
}

const mapCurrentTabToSearchStateKey = (currentTab: SearchResultsTabEnum) => {
  switch (currentTab) {
    case SearchResultsTabEnum.MINDITEMS:
      return 'mindItems';
    case SearchResultsTabEnum.PRODUCTS:
      return 'products';
    case SearchResultsTabEnum.VIDEOS:
      return 'videoMindItems';
    case SearchResultsTabEnum.COURSES:
      return 'courseMindItems';
    case SearchResultsTabEnum.ALL:
      return 'all';
  }
};

export const getCommonCardProps = (item: MindItemSearchResult | ProductSearchResult): MuiCardCommonProps => {
  return {
    id: item.id,
    publisher: item.publisher || '',
    description: item.description || '',
    subjects: item.subjects,
    cover: item.thumbnail,
    title: item.title,
    classYears: item.classYears,
    isNew: item.isNew,
    date: item.publisherReleaseDate,
    isReadOnly: item.isReadOnly,
    isPinned: item.isPinned,
    itemData: item,
  };
};

const debounceTime = 500;
const searchEngineVersion = 2;

const SearchResults: React.FC<SearchResultsProps> = (props) => {
  const cardDimensions = useCardDimensions();

  const mindItemsPagination = useSelector(getPaginationSelector('mindItems'));
  const videoMindItemsPagination = useSelector(getPaginationSelector('videoMindItems'));
  const courseMindItemsPagination = useSelector(getPaginationSelector('courseMindItems'));
  const productsPagination = useSelector(getPaginationSelector('products'));
  const unifiedPagination = useSelector(getUnifiedPaginationSelector);

  const mindItemLoadingState = useSelector(getMindItemLoadingStateSelector);
  const videoMindItemsLoadingState = useSelector(getVideoMindItemsLoadingStateSelector);
  const productLoadingState = useSelector(getProductLoadingStateSelector);
  const courseMindItemsLoadingState = useSelector(getCourseMindItemsLoadingState);
  const unifiedLoadingState = useSelector(getUnifiedLoadingState);

  const [ sentSearchCompleted, setSentSearchCompleted] = useState(false);
  const {
    prefersNewDesign,
  } = useNewDesign();
  const shouldUseNewDesign = props.forceNewDesign || prefersNewDesign;
  const showNewNavigation = useNewNavigation();

  const user = useSelector(selectUser);
  const userActionConfig = {
    hideCollectionAction: user?.hideCollectionAction,
    hidePinAction: user?.hidePinAction,
  };

  const mapTabToPagination = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: mindItemsPagination,
    [SearchResultsTabEnum.PRODUCTS]: productsPagination,
    [SearchResultsTabEnum.VIDEOS]: videoMindItemsPagination,
    [SearchResultsTabEnum.COURSES]: courseMindItemsPagination,
    [SearchResultsTabEnum.ALL]: unifiedPagination,
  }), [
    mindItemsPagination, 
    productsPagination, 
    videoMindItemsPagination, 
    courseMindItemsPagination, 
    unifiedPagination
  ]);

  const mapTabToEventName:{[key: string]: 'document' | 'product' | 'video' | 'course'} = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: 'document',
    [SearchResultsTabEnum.PRODUCTS]: 'product',
    [SearchResultsTabEnum.VIDEOS]: 'video',
    [SearchResultsTabEnum.COURSES]: 'course',
    [SearchResultsTabEnum.ALL]: 'document',
  }), []);

  const mapTabToSearchResultsName = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: 'minditem',
    [SearchResultsTabEnum.PRODUCTS]: 'product',
    [SearchResultsTabEnum.VIDEOS]: 'video',
    [SearchResultsTabEnum.COURSES]: 'course',
    [SearchResultsTabEnum.ALL]: 'document',
  }), []);

  const mapTabToLoadingState = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: mindItemLoadingState,
    [SearchResultsTabEnum.PRODUCTS]: productLoadingState,
    [SearchResultsTabEnum.VIDEOS]: videoMindItemsLoadingState,
    [SearchResultsTabEnum.COURSES]: courseMindItemsLoadingState,
    [SearchResultsTabEnum.ALL]: unifiedLoadingState
  }), [
    mindItemLoadingState,
    productLoadingState,
    videoMindItemsLoadingState,
    courseMindItemsLoadingState,
    unifiedLoadingState
  ]);

  const searchStateKey = mapCurrentTabToSearchStateKey(props.currentTab);
  const saveScrollProperties = (currentTab: SearchResultsTabEnum) => {
    searchResultsStorageManager.setSearchStateInstance(
      mapCurrentTabToSearchStateKey(currentTab),
      mapTabToPagination[currentTab].offset,
      window.scrollY
    );
  };

  const scroll = () => {
    const searchStateInstance = searchResultsStorageManager.getSearchStateInstance(searchStateKey);
    if (!_.isEmpty(searchStateInstance)) {
      const { scrollPosition } = searchStateInstance;
      window.scrollTo(0, scrollPosition);
    } else {
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const resetStorageManager = () => {
      searchResultsStorageManager.clearSearchState();
    };

    window.addEventListener('beforeunload', resetStorageManager);

    return () => {
      window.removeEventListener('beforeunload', resetStorageManager);
    };
  }, []);

  useEffect(() => {
    const handleScroll = debounce(() => {
      saveScrollProperties(props.currentTab);
    }, debounceTime);

    window.addEventListener('scroll', handleScroll);
    scroll();

    return () => {
      handleScroll.cancel();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [props.currentTab]);

  useEffect(() => {
    const statesToCheck = props.tabs.map(tab => mapTabToLoadingState[tab.key]);
    const someInitialStates = statesToCheck.some(state => state === SearchLoadingStatus.INITIAL);

    if (!sentSearchCompleted) {
      const fullyLoaded = statesToCheck.every(state => state === SearchLoadingStatus.LOADED);

      if (fullyLoaded) {
        const searchResultsPayload = props.tabs.reduce((acc, tab) => {
          acc[`nr of ${mapTabToSearchResultsName[tab.key]} search results`] = mapTabToPagination[tab.key].total;
          return acc;
        }, {} as any);
        const ampliSearchEventPayload = getAmpliSearchEventPayload(props.trackingPayload);

        const event = new SearchCompleted({
          'search results tab': mapTabToEventName[props.currentTab],
          ui: mapDesignPreference(shouldUseNewDesign),
          ...searchResultsPayload,
          'search term': ampliSearchEventPayload['search term'],
          'search filter': ampliSearchEventPayload['search filter'],
          'app section': ampliSearchEventPayload['app section'],
        } as any);

        services.track.ampliEventTrack({ event });
        setSentSearchCompleted(true);
      }
    } else if (someInitialStates) {
      setSentSearchCompleted(false);
    }
  }, [
    mapTabToEventName,
    mapTabToPagination,
    mapTabToLoadingState,
    mapTabToSearchResultsName,
    props.currentTab,
    props.tabs,
    props.trackingPayload,
    sentSearchCompleted
  ]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    props.setCurrentTab && props.setCurrentTab(props.tabs[newValue].key);
  };

  const newUiSearchPagePadding = useNewUiSearchPagePadding();

  let searchResultsTabContainerSx = {
    position: 'sticky',
    zIndex: zIndex.searchResultsTab,
  };

  if (shouldUseNewDesign) {
    searchResultsTabContainerSx = Object.assign(searchResultsTabContainerSx, {
      paddingTop: 0,
      background: palette.background.paperElevation1,
    });
  }

  if (showNewNavigation) {
    searchResultsTabContainerSx = Object.assign(searchResultsTabContainerSx, {
      top: 0,
    });
  }

  return (
    <Box 
      className='search-results' 
      data-testid={shouldUseNewDesign ? 'new-ui-search-results' : 'search-results'} 
      sx={{ ...(showNewNavigation && { paddingX: 0 }) }}
    >
      <Box>
        <Tab.Container
          activeKey={props.currentTab}
          onSelect={(key) => props.setCurrentTab && props.setCurrentTab(key as SearchResultsTabEnum)}
        >
          <Box
            sx={searchResultsTabContainerSx}
            className='search-results__tabs-container'
          >
            {
              shouldUseNewDesign ?
                <Box sx={{
                  maxWidth: cardDimensions.desktop.maxWidth,
                  marginX: 'auto',
                  paddingX: newUiSearchPagePadding,
                }}>
                  <MuTabsNewUI tabs={props.tabs} handleChange={handleChange} value={props.currentTab} />
                </Box> :
                <MuTabs tabs={props.tabs} />
            }
          </Box>
          {
            shouldUseNewDesign && props.tabs.length > 1 && 
            <Divider sx={{ 
              position: 'relative',
            }} />
          }
          <Box className='search-results__items' sx={shouldUseNewDesign ? { 
            maxWidth: cardDimensions.desktop.maxWidth,
            marginX: 'auto',
            paddingX: newUiSearchPagePadding,
          } : {}}>
            <Tab.Content>
              {props.tabs.map((tab) => 
                <SearchResultsTab 
                  key={tab.key}
                  searchResultsTab={tab.key}
                  searchTerm={props.searchTerm} 
                  requestFilters={props.requestFilters} 
                  searchEngineVersion={searchEngineVersion} 
                  loadMore={tab.loadMore} 
                  userActionConfig={userActionConfig}
                  currentTab={props.currentTab}
                />
              )}
            </Tab.Content>
          </Box>
        </Tab.Container>
      </Box>
    </Box>
  );
};

export default SearchResults;
